import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, PartyPopper, Candy, Users, GraduationCap, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/oxkzqne6_logo_white_background.jpg";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/#services', label: t('nav.services') },
    { href: '/#gallery', label: t('nav.gallery') },
    { href: '/#about', label: t('nav.about') },
    { href: '/#contact', label: t('nav.contact') },
  ];

  const bookingLinks = [
    { href: '/book/birthday', label: t('footer.birthdayPackage'), icon: PartyPopper },
    { href: '/book/mascot', label: t('footer.mascotReservation'), icon: Users },
    { href: '/book/candy-bar', label: t('footer.candyBar'), icon: Candy },
    { href: '/register/stem', label: t('footer.stemEducation'), icon: GraduationCap },
  ];

  return (
    <>
      <motion.header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center gap-2 bg-white px-3 py-2 rounded-2xl shadow-lg">
              <img 
                src={LOGO_URL} 
                alt="Mascot&Candy Logo" 
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-body font-semibold text-gray-600 hover:text-brand-orange transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA Buttons & Language Toggle */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors font-heading font-bold text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'FR' : 'EN'}
              </button>
              
              <Link to="/book/birthday">
                <Button 
                  data-testid="book-now-btn"
                  className="bg-brand-orange hover:bg-orange-600 text-white rounded-full px-6 py-2 font-heading font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                >
                  {t('nav.bookNow')}
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              {/* Mobile Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors font-heading font-bold text-sm"
              >
                {language === 'en' ? 'FR' : 'EN'}
              </button>
              
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div 
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="absolute top-20 right-0 bottom-0 w-80 max-w-full bg-white shadow-2xl"
            >
              <div className="p-6 space-y-6">
                {/* Nav Links */}
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                    {language === 'en' ? 'Navigate' : 'Navigation'}
                  </p>
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="block py-2 font-body font-semibold text-gray-700 hover:text-brand-orange transition-colors"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>

                {/* Booking Links */}
                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                    {language === 'en' ? 'Book a Service' : 'Réserver un Service'}
                  </p>
                  {bookingLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 py-2 font-body font-semibold text-gray-700 hover:text-brand-orange transition-colors"
                    >
                      <link.icon className="w-5 h-5 text-brand-orange" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
